import React, { useState, useEffect } from 'react';
import { Dropdown, Icon } from '@statsbomb/kitbag-components';
import { useQuery, useReactiveVar, gql } from '@apollo/client';
import { isEmpty, isEqual } from 'lodash';
// eslint-disable-next-line import/no-unresolved
import { Radar } from '@statsbomb/kitbag-datavis';
import { useTheme } from 'styled-components';
import {
  SummaryTile,
  SummaryTileBody,
  SummaryTileCol,
  SummaryTileFooter,
  SummaryTileHeader,
  SummaryError,
} from '../../../components/Tile/TileSummary.styles';
import { mf_PlayerDetails, mf_TeamDetails } from '../../../apollo';
import GoTo from '../../../components/buttons/GoTo';
import {
  getRadarStatsLeagueAverageQuery,
  getRadarStatsQuery,
} from '../PlayerRadar/getPlayerRadarStats';
import { useGetTemplateDefinitions } from '../PlayerRadar/PlayerRadar.hooks';
import { formatTemplateStatsAsRadarAxes } from '../PlayerRadar/PlayerRadar.dataManipulation';
import useQueryString from '../../../utils/hooks/useQueryString';
import { ERROR_CODES } from '../../../utils/errorCodes/errorCodes';
import {
  OptionWithIcon,
  SingleValueWithIcon,
} from '../../../components/DropdownFilter/DropdownFilter.components';
import { PLAYER_RADAR_CHART_OPTIONS } from './PlayerRadar.Tile.constants';
import { RadarTileContainer } from './PlayerRadar.Tile.styles';
import { DropdownWrapper } from '../../../components/DropdownFilter/Dropdown.styles';
import LoaderInline from '../../../components/LoaderInline/LoaderInline';
import { useStore } from '../../../store/useStore';
import { loadingRadarMsg, parseRadarStats } from './PlayerRadar.Tile.helpers';
import Dimmer from '../../../components/Dimmer/Dimmer';
import {
  playerDetailsPropTypes,
  teamDetailsPropTypes,
} from '../../../apollo/index.data.propTypes';
import SimpleRadarKey from '../../../visualisations/RadarChart/SimpleRadarKey';
import { getPalette } from '../../../utils/visualisations/visPalettes';

const PlayerRadarTile = function ({
  __storybookPlayerDetails,
  __storybookTeamDetails = null,
}) {
  // selected player
  const mf_PlayerDetailsRV =
    __storybookPlayerDetails || useReactiveVar(mf_PlayerDetails);
  const mf_TeamDetailsRV =
    __storybookTeamDetails || useReactiveVar(mf_TeamDetails);
  const [showLeagueAverage, setShowLeagueAverage] = useQueryString(
    'avg',
    false,
  );

  const { isDark } = useTheme();
  const visPalette = getPalette(isDark);
  const teamColors = {
    primary: mf_TeamDetailsRV?.colorPrimary,
    secondary: mf_TeamDetailsRV?.colorSecondary,
    tertiary: mf_TeamDetailsRV?.colorTertiary,
  };

  const {
    templates,
    loading: isConfigLoading,
    error: configError,
  } = useGetTemplateDefinitions();

  const [selectedTemplateName, setSelectedTemplateName] = useState(null);
  /* Can't set the default selectedTemplate until you have the player's details */
  useEffect(() => {
    const playerRole = mf_PlayerDetailsRV?.mostCommonPosition?.generalPosition;

    if (playerRole && templates) {
      const defaultTemplate =
        (playerRole &&
          templates?.find((t) => t.defaultFor.includes(playerRole))) ||
        templates?.[0];

      setSelectedTemplateName(defaultTemplate?.name);
    }
  }, [mf_PlayerDetailsRV, isConfigLoading]);

  /* Selected template and stats */
  const selectedTemplate = templates?.find(
    (t) => t.name === selectedTemplateName,
  );
  const templateNames = selectedTemplate?.stats
    ?.concat(selectedTemplate?.secondaryStats)
    ?.map((m) => m.name);

  /* From the stats, get the player and League Average values */
  const playerStatsGQLString = getRadarStatsQuery(templateNames);
  const playerStatsLAGQLString = getRadarStatsLeagueAverageQuery(templateNames);
  const defaultRosterPositions = selectedTemplate?.defaultFor;

  const {
    data: dataStats,
    loading: isStatsLoading,
    error: statsError,
  } = useQuery(gql(playerStatsGQLString), {
    variables: {
      specificPositions: [],
    },
    headers: {
      'Accept-Version': 'v2023q2',
    },
  });
  const isError = !!statsError || !!configError;
  const isLoading = isConfigLoading || isStatsLoading;
  if (statsError) {
    console.error(ERROR_CODES.GET_RADAR_STATS, statsError);
  }
  const {
    data: dataStatsLA,
    loading: statsLoadingLA,
    error: statsLAError,
  } = useQuery(gql(playerStatsLAGQLString), {
    skip: !showLeagueAverage,
    variables: {
      specificPositions: [],
      rosterPositions: defaultRosterPositions,
    },
    headers: {
      'Accept-Version': 'v2023q2',
    },
  });
  if (statsLAError) {
    console.error(ERROR_CODES.GET_RADAR_STATS_LA, statsLAError);
  }

  const radarStats = dataStats?.playerStats?.items?.[0];
  const radarStatsLA = dataStatsLA?.playerStats?.items?.[0];

  // Extract trait and performance data
  const {
    radarTraitStats,
    radarPerfStats,
    radarTraitStatsLA,
    radarPerfStatsLA,
  } = parseRadarStats(radarStats, selectedTemplate, radarStatsLA);

  const radarTraitComparison = showLeagueAverage && radarTraitStatsLA;
  const radarPerfComparison = showLeagueAverage && radarPerfStatsLA;

  const templateConfigTrait = formatTemplateStatsAsRadarAxes(
    selectedTemplate?.secondaryStats,
  );
  const templateConfigPerf = formatTemplateStatsAsRadarAxes(
    selectedTemplate?.stats,
  );

  const radarTraitData = useStore(
    (state) => state.playerOverviewRadarTraitData,
  );
  const setRadarTraitData = useStore(
    (state) => state.setPlayerOverviewRadarTraitData,
  );
  const radarPerfData = useStore((state) => state.playerOverviewRadarPerfData);
  const setRadarPerfData = useStore(
    (state) => state.setPlayerOverviewRadarPerfData,
  );

  useEffect(() => {
    if (
      !isEmpty(radarTraitStats) &&
      !isEqual(radarTraitData, radarTraitStats)
    ) {
      setRadarTraitData(
        radarTraitStats,
        templateConfigTrait,
        radarTraitComparison,
      );
    }

    if (!isEmpty(radarPerfStats) && !isEqual(radarPerfData, radarPerfStats)) {
      setRadarPerfData(radarPerfStats, templateConfigPerf, radarPerfComparison);
    }
  }, [dataStats, showLeagueAverage]);

  return (
    <SummaryTile>
      <SummaryTileHeader>
        <div>
          <h3>Radars</h3>
        </div>
        <div>
          {!isLoading && !isError && (
            <DropdownWrapper>
              <Dropdown
                id="playerRadarDrop"
                components={{
                  Option: OptionWithIcon,
                  SingleValue: SingleValueWithIcon,
                }}
                isSearchable={false}
                menuAlignment="right"
                menuPlacement="bottom"
                onChange={(item) => setShowLeagueAverage(item.value === 1)}
                options={PLAYER_RADAR_CHART_OPTIONS}
                value={PLAYER_RADAR_CHART_OPTIONS[showLeagueAverage ? 1 : 0]}
                labelPosition="none"
                label="Player radar display options"
                variant="tertiary"
                size="small"
              />
            </DropdownWrapper>
          )}
          <GoTo
            href={`/player/radar/:leagues/:seasons/:teams/:players${
              showLeagueAverage ? '?average=true' : ''
            }`}
            linkName="Open Player Radar"
            iconSize="small"
          />
        </div>
      </SummaryTileHeader>
      <SummaryTileBody>
        {isLoading && <Dimmer active style={{ maxWidth: '100%' }} />}
        <div>
          {isError && !isLoading && !statsLoadingLA && (
            <SummaryError>
              <Icon size="small" variant="Warning" colour="primary.main" />
              There has been an error.
            </SummaryError>
          )}
          <RadarTileContainer $maxHeight="480px">
            <div>
              <p>Trait</p>
              {!isEmpty(radarTraitData) && (
                <Radar
                  id="radar-trait"
                  data={radarTraitData}
                  primaryColour={teamColors?.primary}
                  secondaryColour={teamColors?.secondary}
                  comparisonColour={teamColors?.secondary}
                  centreCircle="small"
                  width={480}
                />
              )}

              <SimpleRadarKey
                visPalette={visPalette}
                primaryColor={teamColors?.primary}
                secondaryColor={teamColors?.secondary}
                fontScaler={0.6}
                subjectLabel="Player"
                width={230}
                height={25}
                opacity={showLeagueAverage ? 1 : 0}
              />
            </div>
            <div>
              <p>Performance</p>
              {!isEmpty(radarPerfData) && (
                <Radar
                  id="radar-performance"
                  data={radarPerfData}
                  primaryColour={teamColors?.primary}
                  secondaryColour={teamColors?.secondary}
                  comparisonColour={teamColors?.secondary}
                  centreCircle="small"
                  width={480}
                />
              )}

              <SimpleRadarKey
                visPalette={visPalette}
                primaryColor={teamColors?.primary}
                secondaryColor={teamColors?.secondary}
                fontScaler={0.6}
                subjectLabel="Player"
                width={230}
                height={25}
                opacity={showLeagueAverage ? 1 : 0}
              />
            </div>
          </RadarTileContainer>
        </div>
      </SummaryTileBody>
      <SummaryTileFooter $transparentBackground={!isLoading && !statsLoadingLA}>
        <SummaryTileCol>
          {(isLoading || statsLoadingLA) && (
            <LoaderInline
              text={loadingRadarMsg(
                isConfigLoading,
                isStatsLoading,
                statsLoadingLA,
              )}
            />
          )}
        </SummaryTileCol>
        <SummaryTileCol />
      </SummaryTileFooter>
    </SummaryTile>
  );
};

PlayerRadarTile.propTypes = {
  __storybookPlayerDetails: playerDetailsPropTypes,
  __storybookTeamDetails: teamDetailsPropTypes,
};

export default PlayerRadarTile;
